import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Aviso, Botao, Campo, Cartao, EstadoCarregando, Selo } from '../componentes/ui';
import { useEu } from '../hooks/useEu';
import { useIdentidade } from '../hooks/useSessao';
import { nomeDaEmpresa } from '../servicos/eu';
import { requisicaoAutenticada } from '../servicos/http';
import { Pagina } from './Carteira';
import { ErroDaApi } from './ErroDaApi';

/**
 * Perfil do usuário logado.
 *
 * ## O que ele mostra e por quê
 *
 * Nome, e-mail e as empresas com os papéis em cada uma. Os papéis não são
 * decoração: eles explicam por que o menu do fulano tem menos itens que o do
 * colega, que é a dúvida que chega ao suporte.
 *
 * ## A troca de senha
 *
 * Reusa o fluxo de recuperação por código, que já existe e já é público. Uma
 * rota autenticada de "trocar senha sabendo a atual" seria um segundo caminho
 * para o mesmo fim — e caminhos duplicados de credencial é como se acumulam
 * furos: um recebe a correção, o outro não.
 */
export function Perfil(): JSX.Element {
  const eu = useEu();
  const identidade = useIdentidade();

  if (eu.isPending) return <EstadoCarregando contexto="seu perfil" />;

  const dados = eu.data;

  return (
    <Pagina
      titulo="Meu perfil"
      descricao="Quem você é no sistema, a que empresas está vinculado e com que papel em cada uma."
    >
      <div className="grid gap-4 lg:grid-cols-2">
        <Cartao como="section" titulo="Conta">
          <dl className="flex flex-col gap-3">
            <Linha rotulo="Nome">{dados?.nome ?? '—'}</Linha>
            <Linha rotulo="E-mail">
              <span className="valor-fiscal text-sm">{dados?.emailAddress ?? '—'}</span>
            </Linha>
            <Linha rotulo="Perfil">
              <Selo tom={identidade?.isAdmin === true ? 'informativo' : 'neutro'}>
                {identidade?.isAdmin === true ? 'Administrador' : 'Usuário'}
              </Selo>
            </Linha>
          </dl>
        </Cartao>

        <Cartao como="section" titulo="Senha">
          <TrocaDeSenha emailAtual={dados?.emailAddress ?? ''} />
        </Cartao>
      </div>

      <Cartao
        como="section"
        titulo="Empresas vinculadas"
        subtitulo={`${dados?.empresas.length ?? 0} vínculo(s)`}
      >
        {dados === undefined || dados.empresas.length === 0 ? (
          <p className="text-sm text-conteudo-sutil">
            Nenhum vínculo ativo. Sem vínculo, o sistema não tem o que mostrar — fale com o
            administrador da sua empresa.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {dados.empresas.map((empresa) => (
              <li
                key={empresa.businessId}
                className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-borda px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm text-conteudo-primario">
                    {nomeDaEmpresa(empresa)}
                  </p>
                  <p className="mt-0.5 flex flex-wrap gap-1">
                    {empresa.papeis.map((papel) => (
                      <Selo key={papel} tom="neutro" tamanho="pequeno">
                        {humanizarPapel(papel)}
                      </Selo>
                    ))}
                  </p>
                </div>
                <Link
                  to={`/empresas/${empresa.businessId}`}
                  className="text-sm text-acento-ciano underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
                >
                  Ver cadastro
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Cartao>
    </Pagina>
  );
}

/* ─── Troca de senha ──────────────────────────────────────────────────────── */

function TrocaDeSenha(props: { readonly emailAtual: string }): JSX.Element {
  const [codigo, setCodigo] = useState('');
  const [senha, setSenha] = useState('');

  const solicitar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<{ enviado: boolean }>('/auth/recuperar-senha', {
        metodo: 'POST',
        corpo: { emailAddress: props.emailAtual },
      }),
  });

  const confirmar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<unknown>('/auth/recuperar-senha/confirmar', {
        metodo: 'POST',
        corpo: { emailAddress: props.emailAtual, codigo, novaSenha: senha },
      }),
    onSuccess: () => {
      setCodigo('');
      setSenha('');
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-conteudo-secundario">
        A troca passa por um código enviado ao seu e-mail. É o mesmo caminho de quem esqueceu a
        senha — um só, para não haver dois jeitos de trocar credencial.
      </p>

      <div>
        <Botao
          variante="secundario"
          carregando={solicitar.isPending}
          disabled={props.emailAtual === ''}
          onClick={() => solicitar.mutate()}
        >
          Enviar código para {props.emailAtual === '' ? 'meu e-mail' : props.emailAtual}
        </Botao>
      </div>

      {solicitar.isSuccess && (
        <Aviso tom="informacao" titulo="Código solicitado">
          Se o endereço existir, o código chega em instantes. Ele vale por tempo limitado.
        </Aviso>
      )}
      {solicitar.isError && <ErroDaApi titulo="Não foi possível enviar" erro={solicitar.error} />}

      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          confirmar.mutate();
        }}
      >
        <Campo
          rotulo="Código recebido"
          maxLength={8}
          // 8 caracteres alfanuméricos, não dígitos: `inputMode="numeric"`
          // abriria o teclado numérico no celular e travaria as letras.
          ajuda="8 caracteres, como vieram no e-mail."
          className="valor-fiscal w-40 uppercase"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value.toUpperCase())}
        />
        <Campo
          rotulo="Nova senha"
          type="password"
          ajuda="A nova senha passa a valer imediatamente."
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
        />
        <div>
          <Botao
            type="submit"
            disabled={codigo === '' || senha === ''}
            carregando={confirmar.isPending}
          >
            Trocar senha
          </Botao>
        </div>
      </form>

      {confirmar.isSuccess && (
        <Aviso tom="positivo" titulo="Senha trocada">
          As outras sessões continuam válidas até expirarem. Se desconfia de acesso indevido, use
          “Sair de todos os dispositivos”.
        </Aviso>
      )}
      {confirmar.isError && <ErroDaApi titulo="Não foi possível trocar" erro={confirmar.error} />}
    </div>
  );
}

/* ─── Auxiliares ──────────────────────────────────────────────────────────── */

function Linha(props: {
  readonly rotulo: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-conteudo-sutil">{props.rotulo}</dt>
      <dd className="text-sm text-conteudo-primario">{props.children}</dd>
    </div>
  );
}

function humanizarPapel(papel: string): string {
  const t = papel.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}
